import { useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDroppable } from '@dnd-kit/core';
import { api } from '@/lib/api';
import { snap15, type Block } from '@focus/shared';
import { useUI, visibleHourRange } from '@/stores/ui';
import { Icon } from './icon';
import { cn } from '@/lib/cn';
import { BlockContextMenu, type BlockCat } from './block-context-menu';
import { toneFor } from './block-tones';
import { NowLine } from './now-line';
import { SleepOverlay, useSleepSegments } from './sleep-overlay';

const MIN_HOUR_H = 28;
const GUTTER_W_DESKTOP = 72;
const GUTTER_W_MOBILE = 44;
const WEEKDAY_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function useIsMobile() {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

export function DayView() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const GUTTER_W = isMobile ? GUTTER_W_MOBILE : GUTTER_W_DESKTOP;
  const { darkMode, dayOffset, goPrevDay, goNextDay, goToday, zoomLevel, quietHoursEnabled, quietHoursFrom, quietHoursTo } = useUI();
  const { startHour: START_HOUR, endHour: END_HOUR } = visibleHourRange({ quietHoursEnabled, quietHoursFrom, quietHoursTo });
  const HOURS = END_HOUR - START_HOUR;

  const viewDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);

  const mondayForViewDate = useMemo(() => {
    const d = new Date(viewDate);
    const dow = d.getDay();
    const offsetToMon = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + offsetToMon);
    return d;
  }, [viewDate]);

  const weekStart = mondayForViewDate.getTime();
  const dayIdx = ((viewDate.getDay() + 6) % 7);

  const { data: blocks = [] } = useQuery({
    queryKey: ['blocks', weekStart],
    queryFn: () => api.listBlocks(weekStart),
  });
  useQuery({ queryKey: ['tasks'], queryFn: api.listTasks });

  const dayBlocks = useMemo(() => blocks.filter((b) => b.dayIdx === dayIdx), [blocks, dayIdx]);
  const sleepSegments = useSleepSegments(weekStart);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [baseHourH, setBaseHourH] = useState(MIN_HOUR_H);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => {
      const avail = el.clientHeight;
      const fit = Math.floor(avail / HOURS);
      setBaseHourH(Math.max(MIN_HOUR_H, fit));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [HOURS]);
  const hourH = Math.max(MIN_HOUR_H, Math.round(baseHourH * zoomLevel));

  const patchBlock = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Block> }) => api.patchBlock(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', weekStart] }),
  });
  const deleteBlock = useMutation({
    mutationFn: (id: string) => api.deleteBlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', weekStart] }),
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = viewDate.getTime() === today.getTime();

  const totalH = HOURS * hourH;

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div className="grid border-b border-line dark:border-dark-line" style={{ gridTemplateColumns: `${GUTTER_W}px 1fr` }}>
        <div />
        <div className="px-2 md:px-4 py-2 md:py-2.5 flex items-center gap-2 md:gap-3 border-l border-line dark:border-dark-line">
          <span className="text-[16px] md:text-[18px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg">
            {viewDate.getDate()}.
          </span>
          <span className="text-[12.5px] md:text-[14px] font-medium tracking-[-0.1px] text-fg dark:text-dark-fg truncate">
            {WEEKDAY_LONG[dayIdx]}
          </span>
          <span className="hidden md:inline text-[12px] font-mono text-fg dark:text-dark-fg">
            {viewDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
          <div className="ml-auto flex gap-1">
            <button onClick={goPrevDay} className="btn-ghost h-8 px-2" title="Tag zurück"><Icon name="chevronLeft" size={14} /></button>
            <button onClick={goToday} className="btn-ghost h-8 px-2 text-[12px]" title="Heute">Heute</button>
            <button onClick={goNextDay} className="btn-ghost h-8 px-2" title="Tag vor"><Icon name="chevronRight" size={14} /></button>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin">
        <div className="grid relative pt-3" style={{ gridTemplateColumns: `${GUTTER_W}px 1fr`, minHeight: '100%' }}>
          <div
            className="relative border-r border-line dark:border-dark-line bg-paper dark:bg-dark-paper"
            style={{ height: totalH }}
          >
            {Array.from({ length: Math.floor(HOURS) + 1 }, (_, i) => {
              const hour = Math.floor(START_HOUR) + i;
              if (hour > END_HOUR) return null;
              const top = (hour - START_HOUR) * hourH;
              return (
                <div
                  key={hour}
                  className="absolute left-0 right-0 flex items-center justify-end pr-1 md:pr-2 pointer-events-none"
                  style={{ top, height: 0 }}
                >
                  <span className="text-[10px] md:text-[12px] font-mono text-black dark:text-white tabular-nums leading-none bg-paper dark:bg-dark-paper px-1.5 md:px-3 py-0.5">
                    {String(hour).padStart(2, '0')}:00
                  </span>
                </div>
              );
            })}
          </div>
          <DayDropColumn
            blocks={dayBlocks}
            sleepSegments={sleepSegments}
            darkMode={darkMode}
            hourH={hourH}
            startHour={START_HOUR}
            endHour={END_HOUR}
            hours={HOURS}
            isToday={isToday}
            weekStart={weekStart}
            dayIdx={dayIdx}
            onPatch={(id, patch) => patchBlock.mutate({ id, patch })}
            onDelete={(id) => deleteBlock.mutate(id)}
          />
        </div>
      </div>
    </div>
  );
}

function DayDropColumn({
  blocks,
  sleepSegments,
  darkMode,
  hourH,
  startHour,
  endHour,
  hours,
  isToday,
  weekStart,
  dayIdx,
  onPatch,
  onDelete,
}: {
  blocks: Block[];
  sleepSegments: import('./sleep-overlay').SleepSegment[];
  darkMode: boolean;
  hourH: number;
  startHour: number;
  endHour: number;
  hours: number;
  isToday: boolean;
  weekStart: number;
  dayIdx: number;
  onPatch: (id: string, patch: Partial<Block>) => void;
  onDelete: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'day:current',
    data: { type: 'day', weekStart, dayIdx, hourH, startHour, endHour },
  });
  const ref = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      ref.current = el;
    },
    [setNodeRef],
  );

  return (
    <div
      ref={setRef}
      className={cn(
        'relative border-l border-line dark:border-dark-line',
        isOver && 'bg-accentSoft dark:bg-accent/25',
      )}
      style={{ ['--cal-row-h' as any]: `${hourH}px`, height: hours * hourH } as React.CSSProperties}
    >
      <div className="absolute inset-0 cal-grid-line pointer-events-none" />
      <SleepOverlay segments={sleepSegments} dayIdx={dayIdx} hourH={hourH} startHour={startHour} endHour={endHour} />
      {blocks.filter((b) => b.e > startHour && b.s < endHour).map((b) => (
        <DayBlock key={b.id} block={b} hourH={hourH} startHour={startHour} endHour={endHour} darkMode={darkMode} onPatch={onPatch} onDelete={onDelete} dayRef={ref} />
      ))}
      {isToday && <NowLine startHour={startHour} endHour={endHour} hourH={hourH} />}
    </div>
  );
}

function DayBlock({
  block, hourH, startHour, endHour, darkMode, onPatch, onDelete, dayRef,
}: {
  block: Block;
  hourH: number;
  startHour: number;
  endHour: number;
  darkMode: boolean;
  onPatch: (id: string, patch: Partial<Block>) => void;
  onDelete: (id: string) => void;
  dayRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  const [tempS, setTempS] = useState<number | null>(null);
  const [tempE, setTempE] = useState<number | null>(null);
  const [interacting, setInteracting] = useState(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const openModal = useUI((s) => s.openModal);

  const effS = tempS ?? block.s;
  const effE = tempE ?? block.e;
  const top = (effS - startHour) * hourH;
  const height = Math.max(4, (effE - effS) * hourH);
  const tone = toneFor(block.cat);

  const startResizeBottom = (e: React.PointerEvent) => {
    if (block.anchor) return;
    e.stopPropagation(); e.preventDefault();
    setInteracting(true);
    const rect = dayRef.current!.getBoundingClientRect();
    let latestE: number | null = null;
    const move = (ev: PointerEvent) => {
      const y = ev.clientY - rect.top;
      const raw = startHour + y / hourH;
      const snapped = snap15(Math.max(block.s + 0.25, Math.min(endHour, raw)));
      latestE = snapped;
      setTempE(snapped);
    };
    const up = () => {
      setInteracting(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (latestE != null && latestE !== block.e) onPatch(block.id, { e: latestE });
      setTempE(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startResizeTop = (e: React.PointerEvent) => {
    if (block.anchor) return;
    e.stopPropagation(); e.preventDefault();
    setInteracting(true);
    const rect = dayRef.current!.getBoundingClientRect();
    let latestS: number | null = null;
    const move = (ev: PointerEvent) => {
      const y = ev.clientY - rect.top;
      const raw = startHour + y / hourH;
      const snapped = snap15(Math.max(startHour, Math.min(block.e - 0.25, raw)));
      latestS = snapped;
      setTempS(snapped);
    };
    const up = () => {
      setInteracting(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (latestS != null && latestS !== block.s) onPatch(block.id, { s: latestS });
      setTempS(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startMove = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const role = (e.target as HTMLElement).dataset.role;
    if (role === 'resize-top' || role === 'resize-bottom' || role === 'delete') return;
    e.stopPropagation();
    const startPointerY = e.clientY;
    const startPointerX = e.clientX;
    const startS = block.s;
    const dur = block.e - block.s;
    let moved = false;
    let latestS: number | null = null;
    const move = (ev: PointerEvent) => {
      if (block.anchor) return;
      const dy = ev.clientY - startPointerY;
      const dx = ev.clientX - startPointerX;
      if (!moved && Math.abs(dy) < 3 && Math.abs(dx) < 3) return;
      if (!moved) {
        moved = true;
        setInteracting(true);
      }
      const newS = snap15(Math.max(startHour, Math.min(endHour - dur, startS + dy / hourH)));
      latestS = newS;
      setTempS(newS);
      setTempE(newS + dur);
    };
    const up = () => {
      setInteracting(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!moved) {
        openModal('blockDetail', block.id);
      } else if (latestS != null && latestS !== block.s) {
        onPatch(block.id, { s: latestS, e: latestS + dur });
      }
      setTempS(null);
      setTempE(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (block.anchor) return;
    setMenuAt({ x: e.clientX, y: e.clientY });
  };

  const bg = darkMode ? tone.bgDark : tone.bg;
  const ink = darkMode ? tone.inkDark : tone.ink;
  const isTiny = height < 14;
  const isShort = height < 30;

  return (
    <>
      <div
        className={cn(
          'absolute left-2 right-2 rounded-md select-none overflow-hidden',
          isTiny ? 'p-0' : isShort ? 'px-2 py-0' : 'px-3 py-1.5',
          interacting ? 'shadow-pop z-20' : 'shadow-none z-10',
          !interacting && 'transition-[top,height,filter] duration-150 ease-out',
          !block.anchor && 'cursor-grab active:cursor-grabbing hover:brightness-[1.08] dark:hover:brightness-[1.18]',
        )}
        style={{ top, height, background: bg, border: `1px solid ${tone.line}`, borderLeft: `3px solid ${tone.line}`, color: ink }}
        title={isShort ? `${block.t} · ${fmtHour(effS)}–${fmtHour(effE)}` : undefined}
        onPointerDown={startMove}
        onContextMenu={onContextMenu}
      >
        {!isTiny && (isShort ? (
          <div className="flex items-center gap-1.5 font-medium leading-none pointer-events-none h-full text-[11px]">
            {block.anchor && <Icon name="lock" size={10} className="opacity-60 shrink-0" />}
            <span className="truncate whitespace-nowrap">{block.t}</span>
            <span className="ml-auto font-mono opacity-70 text-[10px] shrink-0">{fmtHour(effS)}–{fmtHour(effE)}</span>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-1 font-medium leading-snug pointer-events-none text-[12px]">
              {block.anchor && <Icon name="lock" size={10} className="opacity-60 shrink-0 mt-[3px]" />}
              <span className="break-words min-w-0">{block.t}</span>
            </div>
            <div className="text-[10.5px] font-mono opacity-80 mt-0.5 pointer-events-none">
              {fmtHour(effS)} – {fmtHour(effE)}
            </div>
          </>
        ))}

        {!block.anchor && (
          <>
            <div data-role="resize-top" onPointerDown={startResizeTop}
              className="absolute left-0 right-0 top-0 h-1.5 cursor-ns-resize z-10" style={{ background: 'transparent' }} />
            <div data-role="resize-bottom" onPointerDown={startResizeBottom}
              className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize z-10" style={{ background: 'transparent' }} />
          </>
        )}
      </div>
      {menuAt && (
        <BlockContextMenu
          x={menuAt.x}
          y={menuAt.y}
          current={block.cat as BlockCat}
          onPick={(cat) => {
            onPatch(block.id, { cat });
            setMenuAt(null);
          }}
          onDelete={() => {
            onDelete(block.id);
            setMenuAt(null);
          }}
          onClose={() => setMenuAt(null)}
        />
      )}
    </>
  );
}

function fmtHour(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
